<script setup lang="ts">
import { AggregationFilterEntityOption, Category } from '@shopware-pwa/types'
import { ShopwareSearchParams } from '@shopware-pwa/types'
import { ComputedRef, UnwrapNestedRefs } from 'vue'

const route = useRoute()
const router = useRouter()

const props = defineProps<{
  category: Category
}>()

const order = Array.isArray(route.query.order) ? route.query.order[0] ?? '' : route.query.order ?? ''
const properties = Array.isArray(route.query.properties)
  ? route.query.properties[0] ?? ''
  : route.query.properties ?? ''
const {
  getElements,
  search,
  getCurrentPage,
  changeCurrentPage,
  getTotalPagesCount,
  getAvailableFilters,
  getCurrentFilters,
  filtersToQuery,
  getCurrentSortingOrder,
  getSortingOrders,
  changeCurrentSortingOrder,
} = useListing({
  listingType: 'categoryListing',
  categoryId: props.category.id,
  defaultSearchCriteria: {
    // set the default criteria
    limit: 12,
    p: Number(route?.query?.p) || 1,
    properties: properties,
    order: order,
    associations: {
      properties: {},
    },
  },
})

const sidebarSelectedFilters: UnwrapNestedRefs<{
  [key: string]: any
}> = reactive<{
  properties: Set<any>
}>({
  properties: new Set(),
})

const searchCriteriaForRequest: ComputedRef<{
  [code: string]: string | string[] | number | number[] | boolean | undefined
}> = computed(() => ({
  properties: [...sidebarSelectedFilters.properties],
  order: getCurrentSortingOrder.value,
}))

for (const param in route.query) {
  if (sidebarSelectedFilters.hasOwnProperty(param)) {
    if (sidebarSelectedFilters[param] && typeof sidebarSelectedFilters[param] === 'object') {
      ;(route.query[param] as unknown as string).split('|').forEach((element) => {
        sidebarSelectedFilters[param].add(element)
      })
    } else {
      sidebarSelectedFilters[param] = route.query[param]
    }
  }
}

const updateFilter = async (filter: { code: string; value: string[] }) => {
  sidebarSelectedFilters[filter.code]?.clear()
  filter.value.forEach((item) => sidebarSelectedFilters[filter.code]?.add(item))
  await search({
    ...filtersToQuery(searchCriteriaForRequest.value),
    p: 1,
  })
  await router.push({
    query: {
      ...filtersToQuery(searchCriteriaForRequest.value),
      p: 1,
    },
  })
}

const languageFilter = computed(() => {
  return getAvailableFilters.value.filter((filter) => {
    const firstOption = filter.options?.[0] as AggregationFilterEntityOption
    return (
      firstOption?.translated.customFields?.custom_property_fields_language_property_group_code === 'language-group'
    )
  })
})

const articleTypeFilterOptions = computed(() => {
  return (
    getAvailableFilters.value
      .filter((filter) => {
        const firstOption = filter.options?.[0] as AggregationFilterEntityOption
        return (
          (firstOption?.translated.customFields?.custom_property_fields_type_property_group_code ===
            'product-set-group' ||
            firstOption?.translated.customFields?.custom_property_fields_type_property_group_code ===
              'product-type-group') &&
          filter.options?.length
        )
      })
      // The article type filters contains multiple ListingFilters with the options attribute, which is an array.
      // We need to merge all the options into one array.
      .flatMap((filter) => filter.options ?? [])
      // Sort the options, so that the option with custom_property_fields_type_property_code = 'set' is last.
      .sort((a, b) => {
        const aIsSet = a.translated.customFields?.custom_property_fields_type_property_code === 'set'
        const bIsSet = b.translated.customFields?.custom_property_fields_type_property_code === 'set'
        if (aIsSet && !bIsSet) {
          return 1
        }
        if (!aIsSet && bIsSet) {
          return -1
        }
        return 0
      })
  )
})

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || '',
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
        p: 1,
      },
    })
    changeCurrentSortingOrder(order, route.query as Partial<ShopwareSearchParams>)
  },
})

const sortingOptions: any = computed(() =>
  getSortingOrders.value.map((sorting) => ({
    value: sorting.key,
    label: sorting.label,
  })),
)

const changePage = async (page: number) => {
  await router.push({
    query: {
      ...route.query,
      p: page,
    },
  })
  changeCurrentPage(page, route.query as Partial<ShopwareSearchParams>)
}

await search({})
</script>
<template>
  <div>
    <div v-animate="{ animation: 'vertical-reveal' }" class="filters">
      <div class="grid">
        <div class="tl-5 ds-6">
          <ShopSearch />
        </div>
        <div class="tl-7 ds-6 d-5 d-o-1 filters-wrapper">
          <ShopFilterLanguage
            v-if="languageFilter.length"
            :key="languageFilter[0].code"
            :filter="languageFilter[0]"
            :current-filters="getCurrentFilters"
            @select-value="updateFilter"
          />
          <Select
            v-model="currentSortingOrder"
            name="sorting"
            :required="false"
            label="Sortierung"
            :options="sortingOptions"
          />
        </div>
      </div>
      <ShopFilterArticleType
        v-if="articleTypeFilterOptions.length"
        :filter-options="articleTypeFilterOptions"
        :current-filters="getCurrentFilters"
        @select-value="updateFilter"
      />
    </div>
    <Products v-if="getElements.length" :products="getElements" />
    <div v-else>{{ $t('shop.listingPage.noProductsMessage') }}</div>
    <Pagination
      v-if="getTotalPagesCount > 1"
      :page="Number(getCurrentPage)"
      :max="getTotalPagesCount"
      @update:page="changePage"
    />
  </div>
</template>
<style scoped lang="scss">
.products {
  margin-bottom: 64px;
}
.filters {
  padding: 24px 0;
  border-top: 1px solid var(--c-line-light);
  border-bottom: 1px solid var(--c-line-light);
  @include fluid(margin-bottom, 32px, 96px);
  .grid {
    row-gap: 16px;
    margin-bottom: 32px;
  }
  .filters-wrapper {
    display: grid;
    grid-template-columns: 100%;
    gap: 16px;
    @include breakpoint(ts) {
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
